import React from "react";
import { Tooltip, radius } from "@libertify/ui";
import { Flex } from "antd";
import styled from "styled-components";
import { analyticsTheme } from "./analyticsTheme";

export type ItemRow = {
  key: string;
  label?: string;
  text: string;
  tags?: { text: string; danger?: boolean }[];
  /** An item nobody touched keeps its row, greyed out. */
  muted?: boolean;
  /** A hard question gets a red number. */
  hard?: boolean;
  value: React.ReactNode;
  tooltip: string;
  /** With `trackColor`, the bar is `color` over `percent` and `trackColor` over the rest. */
  bar: { percent: number; color: string; trackColor?: string } | null;
};

export type ItemRowsProps = { rows: ItemRow[] };

const clamp = (n: number) => Math.min(100, Math.max(0, n));

export const ItemRows: React.FC<ItemRowsProps> = ({ rows }) => {
  if (rows.length === 0) return null;
  // Without labels, drop the number column so the rows start at the card edge.
  const numbered = rows.some((row) => row.label);
  return (
    <Flex vertical gap={10}>
      {rows.map(({ bar, ...row }) => (
        <Tooltip key={row.key} content={row.tooltip} placement="top">
          <StyledItem $numbered={numbered}>
            {numbered ? <StyledNumber $hard={row.hard}>{row.label}</StyledNumber> : null}
            <Flex
              wrap
              gap={6}
              align="baseline"
              style={{
                minWidth: 0,
                overflowWrap: "anywhere",
                color: row.muted ? analyticsTheme.textTick : analyticsTheme.textBody,
              }}
            >
              {row.text}
              {row.tags?.map((tag) => (
                <StyledTag key={tag.text} $danger={tag.danger}>
                  {tag.text}
                </StyledTag>
              ))}
            </Flex>
            <StyledValue>{row.value}</StyledValue>
            {bar ? (
              // Two segments with a small gap, as in the handoff: the share, then the rest.
              <StyledTrack
                role="progressbar"
                aria-valuenow={Math.round(clamp(bar.percent))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span style={{ width: `${clamp(bar.percent)}%`, background: bar.color }} />
                {bar.trackColor && bar.percent < 100 ? (
                  <span style={{ flex: 1, background: bar.trackColor }} />
                ) : null}
              </StyledTrack>
            ) : (
              <span />
            )}
          </StyledItem>
        </Tooltip>
      ))}
    </Flex>
  );
};

// ─── Styled components ───

const StyledItem = styled.div<{ $numbered: boolean }>`
  display: grid;
  grid-template-columns: ${({ $numbered }) =>
    $numbered ? "34px minmax(0, 1fr) 96px" : "minmax(0, 1fr) 96px"};
  gap: 4px 10px;
  align-items: center;
  font-size: 12.5px;
`;

const StyledNumber = styled.span<{ $hard?: boolean }>`
  grid-row: span 2;
  align-self: start;
  font-weight: 700;
  color: ${({ $hard }) => ($hard ? analyticsTheme.metricError : analyticsTheme.textBody)};
`;

const StyledTag = styled.span<{ $danger?: boolean }>`
  font-size: 10.5px;
  font-weight: 600;
  border-radius: ${radius.pill}px;
  padding: 0 7px;
  white-space: nowrap;
  background: ${({ $danger }) => ($danger ? "#fee2e2" : "#f3f4f6")};
  color: ${({ $danger }) => ($danger ? analyticsTheme.metricError : analyticsTheme.textLabel)};
`;

const StyledValue = styled.span`
  grid-row: span 2;
  align-self: start;
  text-align: right;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: ${analyticsTheme.textMuted};

  strong {
    font-weight: 600;
    color: ${analyticsTheme.textTitle};
  }
`;

const StyledTrack = styled.div`
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: ${radius.pill}px;
  overflow: hidden;
  background: ${analyticsTheme.barTrack};

  > span {
    display: block;
    height: 100%;
  }
`;
